/* eslint-disable react-hooks/exhaustive-deps */
import { SelectOption } from 'models/formSingleSelectField';
import { useField, useFormikContext } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAfterMountEffect } from '../../../../helpers/useAfterMountEffect';
import { FormDateField } from '../../../form/FormDateField';
import { WrappedFormSingleSelectField } from '../../../form/FormSingleSelectField';
import FormTextField from '../../../form/FormTextField';
import { FormValues, OwnerTreeStructure } from '../../schema';
import Button from '../../../button/Button';
import instance from '../../../../api';
import { endpoints } from '../../../../endpoints.config';
import { Toast, ToastMessageReason } from '../../../../helpers/toast';
import { formatValues } from '../helpers';
import { getDirectOwnersPercentageTotal, getParentTreeFieldname } from './helpers';
import { useVerificationContext } from '../verificationContext';
import UploadField from '../UploadField';

export type BusinessOwnerProps = {
    fieldName: string;
    treeFieldname: string;
    availableCountries: (SelectOption & { states: SelectOption[] | null })[];
    entityTypes: SelectOption[];

    onClose: () => void;
};

const operatingAddressFieldNames = [
    'operatingAddressStreet',
    'operatingAddressNumber',
    'operatingAddressPostCode',
    'operatingAddressCity',
    'operatingAddressState',
    'operatingAddressCountry',
];

export const BusinessOwner: React.FC<BusinessOwnerProps> = ({
    fieldName,
    treeFieldname,
    availableCountries,
    entityTypes,
    onClose,
}) => {
    const { setFieldValue, setFieldTouched, validateForm } = useFormikContext<FormValues>();
    const [{ value }, , { setValue }] = useField(fieldName);
    const [{ value: countryValue }] = useField<any[]>(`${fieldName}.addressCountry`);
    const [{ value: operatingCountryValue }] = useField<any[]>(
        `${fieldName}.operatingAddressCountry`
    );
    const [{ value: entityTypesIdValue }] = useField<any[]>(`${fieldName}.entityTypesID`);
    const [{ value: treeValue }, , { setValue: setTreeValue }] = useField(treeFieldname);
    const parentTreeFieldname = getParentTreeFieldname(treeFieldname);
    const [{ value: parentTreeValue }] = useField<OwnerTreeStructure | OwnerTreeStructure[]>(
        parentTreeFieldname
    );

    // Store initial state in case they want to close the form rather than save
    const [initialValues] = useState({ business: value, treeValue });
    // Set to true only on a successful Save; anything else (X, Escape, Close) is a discard.
    const savedRef = useRef(false);

    // Revert to the pre-edit values on any close that isn't a successful save, so filling the
    // form and closing it (e.g. via the modal's X) doesn't leave the owner looking "complete"
    // and flip its button from "Owner Data Required" to "Edit Owners Data".
    useEffect(() => {
        return () => {
            if (!savedRef.current) {
                setValue(initialValues.business);
                setTreeValue(initialValues.treeValue);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const siblings = Array.isArray(parentTreeValue)
        ? parentTreeValue
        : parentTreeValue?.children ?? [];
    const siblingTotal = getDirectOwnersPercentageTotal(siblings);
    // If this field's own value already exceeds 100, that's already flagged by its own
    // max(100) validation — don't also show the redundant aggregate-total message.
    const ownValueExceeds100 = (treeValue?.percentageOwned ?? 0) > 100;
    // Business owners can never be a controlling party, so 0% is never allowed here.
    const ownValueIsZeroOrLess =
        treeValue?.percentageOwned != null && treeValue.percentageOwned <= 0;
    const exceedsTotalPercentage = siblingTotal > 100 && !ownValueExceeds100;

    const handleCloseWithoutSave = () => {
        setValue(initialValues.business);
        setTreeValue(initialValues.treeValue);
        onClose();
    };

    // On (re)open, clear any stale "touched" state for this owner so fields that
    // were filled then discarded on a previous open don't immediately show
    // required errors before the user interacts with them again.
    useEffect(() => {
        setFieldTouched(fieldName, false, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stateOptions = useMemo(() => {
        return availableCountries.find((country) => country.value === countryValue)?.states ?? null;
    }, [availableCountries, countryValue]);

    const operatingStateOptions = useMemo(
        () =>
            availableCountries.find((country) => country.value === operatingCountryValue)?.states ??
            null,
        [availableCountries, operatingCountryValue]
    );

    // Reset field when the country changes
    useAfterMountEffect(() => {
        setFieldValue(`${fieldName}.addressState`, '');
    }, [stateOptions]);
    // Reset field when the country changes
    useAfterMountEffect(() => {
        setFieldValue(`${fieldName}.operatingAddressState`, '');
    }, [operatingStateOptions]);

    const isOtherEntityType =
        entityTypesIdValue &&
        entityTypesIdValue == entityTypes?.find((type) => type.label === 'Other')?.value;

    const isOperatingAddressStarted = !!(
        value.operatingAddressStreet ||
        value.operatingAddressNumber ||
        value.operatingAddressPostCode ||
        value.operatingAddressCity ||
        value.operatingAddressState ||
        value.operatingAddressCountry
    );
    const operatingAddressLabelExtraInfo = isOperatingAddressStarted ? undefined : 'Optional';

    // Once any operating address field is started, touch them all so the
    // now-required fields show their validation errors immediately, not just
    // the ones the user happened to focus/blur.
    useEffect(() => {
        if (isOperatingAddressStarted) {
            operatingAddressFieldNames.forEach((field) =>
                setFieldTouched(`${fieldName}.${field}`, true, false)
            );
        }
    }, [isOperatingAddressStarted]);

    const { values } = useFormikContext<FormValues>();
    const { selectOptions } = useVerificationContext();

    const getDocumentId = (internalName: string): number | undefined => {
        const match = selectOptions?.openedDocumentsList?.find(
            (doc) => doc.internalName === internalName
        );
        return match?.id;
    };

    const businessFormationDocumentId = getDocumentId('BBOBusinessFormationDocument');
    const companyUboCertificateOfGoodStandingId = getDocumentId(
        'BBOCompanyUboCertificateOfGoodStanding'
    );
    const companyUboW9FormId = getDocumentId('BBOCompanyUboW9Form');
    const companyUboW8FormId = getDocumentId('BBOCompanyUboW8Form');
    const businessProofOfAddressId = getDocumentId('BBOProofOfAddress');

    const hasRequiredDocuments =
        !!(
            value?.businessFormationDocumentFilename?.trim?.() &&
            value?.companyUboCertificateOfGoodStandingFilename?.trim?.() &&
            value?.companyUboW9FormFilename?.trim?.() &&
            value?.proofOfAddressFilename?.trim?.()
        );

    const hasRequiredFields = !!(
        value?.entityName?.trim?.() &&
        value?.entityTypesID != null &&
        value?.registrationDate != null &&
        value?.registrationDate !== '' &&
        /^\d+$/.test(String(value?.registrationNumber ?? '').trim()) &&
        /^\d+$/.test(String(value?.officialBusinessRegistrationNumber ?? '').trim()) &&
        value?.taxNumber?.trim?.() &&
        value?.doingBusinessAs?.trim?.() &&
        value?.addressStreet?.trim?.() &&
        value?.addressNumber?.trim?.() &&
        value?.addressPostCode?.trim?.() &&
        value?.addressCity?.trim?.() &&
        value?.addressState?.trim?.() &&
        value?.addressCountry?.trim?.()
    );

    const canSave =
        hasRequiredDocuments &&
        hasRequiredFields &&
        !exceedsTotalPercentage &&
        !ownValueExceeds100 &&
        !ownValueIsZeroOrLess;

    const saveForm = async (): Promise<boolean> => {
        try {
            const formattedValues = formatValues(
                values,
                values.businessVerificationStep
            ) as FormValues & {
                businessVerificationNextStep?: typeof values.businessVerificationStep;
            };
            formattedValues.businessVerificationStep = values.businessVerificationStep;
            formattedValues.businessVerificationNextStep = values.businessVerificationStep;
            await instance.post(
                endpoints.businessVerificationV3Module.saveFormData,
                formattedValues
            );
            return true;
        } catch (error: any) {
            Toast.openToastMessage(
                'Error saving form, please try again.',
                ToastMessageReason.ERROR
            );
            return false;
        }
    };

    const handleSave = async () => {
        const success = await saveForm();
        if (success) {
            savedRef.current = true;
            onClose();
        }
    };

    return (
        <div className="Form">
            <h1>Add Beneficial Owner Information</h1>
            <FormTextField field={`${fieldName}.entityName`} label={'Legal Name of Entity*'} />
            <div className="FormSideBySide">
                <div className="FormBuilderComponent SideBySide">
                    <WrappedFormSingleSelectField
                        fieldName={`${fieldName}.entityTypesID`}
                        options={entityTypes}
                        label={'Type of Entity*'}
                    />
                </div>

                <div className="FormBuilderComponent SideBySide">
                    <FormDateField
                        field={`${fieldName}.registrationDate`}
                        label={'Date of Entity Registration*'}
                    />
                </div>
            </div>
            {isOtherEntityType && (
                <FormTextField field={`${fieldName}.entityTypesOther`} label={'Entity Type*'} />
            )}
            <div className="FormSideBySide">
                <FormTextField
                    field={`${fieldName}.officialBusinessRegistrationNumber`}
                    label={'Official Business Registration Number*'}
                    sideBySide
                    numbersOnly
                />
                <FormTextField
                    field={`${fieldName}.taxNumber`}
                    label={'Tax ID Number*'}
                    sideBySide
                />
            </div>
            <FormTextField
                field={`${fieldName}.registrationNumber`}
                label={'Registration Number*'}
                numbersOnly
            />
            <FormTextField
                type="textarea"
                field={`${fieldName}.doingBusinessAs`}
                label={'Doing Business As - DBA (Please list all business names.) *'}
            />
            <div className="FormSideBySide">
                <FormTextField
                    field={`${fieldName}.addressStreet`}
                    label={'Registered Address Street'}
                    sideBySide
                />
                <FormTextField
                    field={`${fieldName}.addressNumber`}
                    label={'Registered Address Unit Number'}
                    sideBySide
                />
            </div>
            <div className="FormSideBySide">
                <FormTextField
                    field={`${fieldName}.addressPostCode`}
                    label={'Registered Address Postal Code'}
                    sideBySide
                />
                <FormTextField
                    field={`${fieldName}.addressCity`}
                    label={'Registered Address City'}
                    sideBySide
                />
            </div>
            <div className="FormSideBySide">
                {stateOptions ? (
                    <div className="FormBuilderComponent SideBySide">
                        <WrappedFormSingleSelectField
                            fieldName={`${fieldName}.addressState`}
                            options={stateOptions}
                            label={
                                'Registered Address State / County / Province (as applicable to the jurisdiction)'
                            }
                        />
                    </div>
                ) : (
                    <FormTextField
                        field={`${fieldName}.addressState`}
                        label={
                            'Registered Address State / County / Province (as applicable to the jurisdiction)'
                        }
                        sideBySide
                    />
                )}
                <div className="FormBuilderComponent SideBySide">
                    <WrappedFormSingleSelectField
                        fieldName={`${fieldName}.addressCountry`}
                        options={availableCountries}
                        label={'Registered Country'}
                    />
                </div>
            </div>
            <div className="FloatingErrorField">
                <FormTextField
                    field={`${treeFieldname}.percentageOwned`}
                    label={'Percentage of Shares Owned (%)*'}
                    type="number"
                    maxLength={200}
                    hideError
                    className={
                        exceedsTotalPercentage || ownValueExceeds100 || ownValueIsZeroOrLess
                            ? 'ErrorInput'
                            : ''
                    }
                />
                {ownValueExceeds100 && (
                    <div className="ErrorLabel">
                        Percentage of shares owned cannot exceed 100%
                    </div>
                )}
                {ownValueIsZeroOrLess && !ownValueExceeds100 && (
                    <div className="ErrorLabel">
                        Percentage of shares owned must be greater than 0%
                    </div>
                )}
                {!ownValueExceeds100 && !ownValueIsZeroOrLess && exceedsTotalPercentage && (
                    <div className="ErrorLabel">
                        The combined ownership percentage of beneficial owners cannot exceed 100%
                    </div>
                )}
            </div>
            <h3>Business Operating Information (If different than registerd information.)</h3>
            <div className="FormSideBySide">
                <FormTextField
                    field={`${fieldName}.operatingAddressStreet`}
                    label={'Operating Address Street'}
                    labelExtraInfo={operatingAddressLabelExtraInfo}
                    sideBySide
                />
                <FormTextField
                    field={`${fieldName}.operatingAddressNumber`}
                    label={'Operating Address Unit Number'}
                    labelExtraInfo={operatingAddressLabelExtraInfo}
                    sideBySide
                />
            </div>
            <div className="FormSideBySide">
                <FormTextField
                    field={`${fieldName}.operatingAddressPostCode`}
                    label={'Operating Address Postal Code'}
                    labelExtraInfo={operatingAddressLabelExtraInfo}
                    sideBySide
                />
                <FormTextField
                    field={`${fieldName}.operatingAddressCity`}
                    label={'Operating City'}
                    labelExtraInfo={operatingAddressLabelExtraInfo}
                    sideBySide
                />
            </div>
            <div className="FormSideBySide">
                {operatingStateOptions ? (
                    <div className="FormBuilderComponent SideBySide">
                        <WrappedFormSingleSelectField
                            fieldName={`${fieldName}.operatingAddressState`}
                            options={operatingStateOptions}
                            label={
                                'Operating State / County / Province (as applicable to the jurisdiction)'
                            }
                            labelExtraInfo={operatingAddressLabelExtraInfo}
                        />
                    </div>
                ) : (
                    <FormTextField
                        field={`${fieldName}.operatingAddressState`}
                        label={
                            'Operating State / County / Province (as applicable to the jurisdiction)'
                        }
                        labelExtraInfo={operatingAddressLabelExtraInfo}
                        sideBySide
                    />
                )}
                <div className="FormBuilderComponent SideBySide">
                    <WrappedFormSingleSelectField
                        fieldName={`${fieldName}.operatingAddressCountry`}
                        options={availableCountries}
                        label={'Operating Country'}
                        labelExtraInfo={operatingAddressLabelExtraInfo}
                        dropdownProps={{ isClearable: true }}
                    />
                </div>
            </div>
            <div className="NewBusinessFormRow">
                <div className="FormBox">
                    <div className="FormLabel">
                        <label>
                            Business Formation Document
                            <span>*</span>
                        </label>
                    </div>
                    <UploadField
                        fieldName={`${fieldName}.businessFormationDocumentFilename`}
                        displayError={true}
                        jsonField={true}
                        documentId={businessFormationDocumentId}
                    />
                </div>
                <div className="FormBox">
                    <div className="FormLabel">
                        <label>
                            Company UBO Certificate of Good Standing
                            <span>*</span>
                        </label>
                    </div>
                    <UploadField
                        fieldName={`${fieldName}.companyUboCertificateOfGoodStandingFilename`}
                        displayError={true}
                        jsonField={true}
                        documentId={companyUboCertificateOfGoodStandingId}
                    />
                </div>
            </div>
            <div className="NewBusinessFormRow">
                <div className="FormBox">
                    <div className="FormLabel">
                        <label>
                            Company UBO Form - W-9 (US) / W-8 (Non-US)
                            <span>*</span>
                        </label>
                    </div>
                    <UploadField
                        fieldName={`${fieldName}.companyUboW9FormFilename`}
                        displayError={true}
                        jsonField={true}
                        documentId={companyUboW9FormId}
                    />
                </div>
            </div>
            <div className="NewBusinessFormRow">
                <div className="FormBox">
                    <div className="FormLabel">
                        <label>
                            Proof of Address
                            <span>*</span>
                        </label>
                    </div>
                    <UploadField
                        fieldName={`${fieldName}.proofOfAddressFilename`}
                        displayError={true}
                        jsonField={true}
                        documentId={businessProofOfAddressId}
                    />
                </div>
            </div>
            <div className="BusinessOwnerButtons">
                <Button onClick={handleCloseWithoutSave} type="button" priority="secondary">
                    Close
                </Button>
                <Button onClick={handleSave} type="button" disabled={!canSave}>
                    Save
                </Button>
            </div>
        </div>
    );
};
